<template>
  <div class="modal-card spacing-px-30">
    <b-loading data-cy="LOADING" isFullPage :active="isLoadingFull" />
    <span
      class="has-text-centered has-text-weight-semibold has-text-color-8 spacing-mb-20"
      >{{ General.PROCESS_DOCUMENTS }}</span
    >
    <div class="columns mt-10">
      <b-field class="column is-8 is-9-desktop">
        <b-input
          v-model="documentSearch"
          :placeholder="General.PLACEHOLDER.TYPE_TO_SEARCH"
          type="search"
          icon="magnify"
          iconClickable
          @input="setFilterDocumentSearch"
        />
      </b-field>
      <div class="column is-4 is-3-desktop">
        <b-field
          :label="General.DOCUMENT_TYPE"
          labelPosition="on-border"
          class="select-field"
        >
          <b-select
            v-model="typeSearch"
            :disabled="Boolean(defaultDocumentType)"
            :placeholder="General.SELECT_TYPE"
            @input="setFilterDocumentType"
          >
            <option :value="{}" aria-role="listitem">{{ General.ALL }}</option>
            <option
              v-for="option in getValidFileTypes"
              :key="option.id"
              :value="option"
            >
              {{ option.type }}
            </option>
          </b-select>
        </b-field>
      </div>
    </div>
    <div class="list-document">
      <b-table
        :data="documentFiltered"
        :rowClass="() => 'is-clickable'"
        @click="selectDocument"
      >
        <template>
          <b-table-column
            v-slot="props"
            :label="General.NAME"
            field="attachment_name"
          >
            <div class="is-flex is-align-items-center">
              <b-icon
                class="spacing-mr-10"
                :type="isContrast ? 'is-white' : 'is-primary'"
                icon="file-document"
                size="is-small"
              />

              <b-tooltip
                :active="props.row.attachment_name.length > 40"
                :type="isContrast ? 'is-white' : 'is-blue-oil'"
                :label="props.row.attachment_name"
                multilined
                size="is-large"
              >
                <span class="has-text-color-7">{{
                  truncate(props.row.attachment_name, { length: 40 })
                }}</span>
              </b-tooltip>
            </div>
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="General.CREATED_BY"
            field="capacity"
          >
            <span class="has-text-color-7">{{
              props.row.capacityAbbreviation
            }}</span>
          </b-table-column>
          <b-table-column v-slot="props" :label="General.ADDED_ON" field="date">
            <span class="has-text-color-7">{{
              new Date(props.row.date) | dateFormat("DD/MM/YYYY")
            }}</span>
          </b-table-column>
        </template>

        <template #empty>
          <section class="section">
            <NoListMessage
              v-if="!isLoading"
              :message="General.TEXTS.NO_DOCUMENTS"
            />
            <b-loading v-else :isFullPage="false" :active="isLoading" />
          </section>
        </template>
      </b-table>
    </div>
    <div class="mt-20 is-flex is-justify-content-flex-end">
      <b-button
        :type="isContrast ? 'is-white' : 'is-primary'"
        outlined
        @click="close"
      >
        {{ General.CANCEL.toUpperCase() }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { debounce, truncate } from "lodash";
import { mapGetters, mapActions } from "vuex";
import NoListMessage from "@/components/NoListMessage";

import { App, General } from "@/utils/constants";

export default {
  name: "ModalDocumentClone",

  components: {
    NoListMessage
  },

  props: {
    processId: {
      type: String,
      required: true
    },
    documentId: {
      type: String,
      required: true
    },
    defaultDocumentType: {
      type: String,
      default: undefined
    },
    onGetDocument: {
      type: Function,
      default: () => null
    },
    isPrimary: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      App,
      General,
      truncate,
      documentSearch: "",
      documentList: [],
      documentFiltered: [],
      isLoading: false,
      isLoadingFull: false,
      typeSearch: {}
    };
  },

  computed: {
    ...mapGetters({
      getFileTypes: "getFileTypes",
      isContrast: "isContrast"
    }),

    getValidFileTypes() {
      return this.getFileTypes.filter(type => type.layout_template);
    }
  },

  async beforeMount() {
    this.isLoading = true;
    await this.fileTypesRequest({
      state: true,
      excludeRequestTypes: !this.isPrimary
    });
    const documents = await this.getDocumentCloneListRequest({
      processId: this.processId,
      documentType: this.defaultDocumentType
    });
    documents.forEach(document => {
      document.capacityAbbreviation = `${
        document.capacity.entity.abbreviation
      }/${document.capacity.abbreviation.split("/").at(-1)}`;
    });
    this.documentList = documents.filter(
      document => document.id !== this.documentId
    );
    this.documentFiltered = this.documentList ? [...this.documentList] : [];
    this.setInitialType();
    this.isLoading = false;
  },

  methods: {
    ...mapActions({
      fileTypesRequest: "fileTypesRequest",
      getDocumentCloneListRequest: "getDocumentCloneListRequest",
      documentCloneRequest: "documentCloneRequest"
    }),

    setInitialType() {
      if (this.getFileTypes.length && this.defaultDocumentType) {
        this.typeSearch = this.getFileTypes.find(fileType => {
          return fileType.id == this.defaultDocumentType;
        });
      }
    },

    async selectDocument(document) {
      this.isLoadingFull = true;
      const file = await this.documentCloneRequest({
        processFileId: document.id,
        data: {
          attachment_name: document.attachment_name
        }
      });
      file &&
        this.onGetDocument({
          gdocsId: file.id,
          attachmentName: document.attachment_name
        });
      this.isLoadingFull = false;
      this.close();
    },

    close() {
      this.$parent.close();
    },

    setFilterDocumentSearch: debounce(function(search) {
      this.documentFiltered = this.documentList.filter(document =>
        RegExp(search, "i").test(document.attachment_name)
      );
    }, 1000),

    setFilterDocumentType(type) {
      if (!type?.id) {
        this.documentFiltered = this.documentList;
        return;
      }
      this.documentFiltered = this.documentList.filter(
        document => document.document_type === type.id
      );
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.modal-card {
  display: flex;
  justify-content: space-between;
  padding: 15px;
  width: 920px;
  max-width: 50vw;
  max-height: 75vh;
  background-color: $color-1;
}
.list-container {
  overflow: auto;
}

.header-body {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  border-bottom: 1px solid;
  border-bottom-color: #cdcdcd;
}

.list-document {
  overflow: auto;

  #documents-empty {
    text-align: center;
    margin: 10px 0;
  }
}

.document-item {
  display: flex;
  align-items: center;
  padding: 5px;
  margin-top: 5px;
  cursor: pointer;
  &:hover {
    background-color: #f2f2f2;
  }
}

.bb-custom {
  border-bottom: 1px solid #f2f2f2;
}

.custom-self-align-end {
  align-self: flex-end;
}

.label-custom {
  z-index: 1;
  position: absolute;
  font-size: 0.75rem;
  background: white;
}
</style>
